/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import {
  INPUT_TYPE_CONSTANT,
  INPUT_TYPE_DOMAIN,
  COLUMNTYPE_NUMBER,
  COLUMNTYPE_BOOLEAN,
  COLUMNTYPE_TIMESTAMP,
  COLUMNTYPE_LITERAL,
  COLUMNTYPE_JSON,
  COLUMNTYPE_ARRAY,
  INPUT_TYPE_DATA_ITEM,
  KPI_CATEGORY_ALERT,
  KPI_CATEGORY_TRANSFORMER,
  KPI_CATEGORY_AGGREGATOR,
  KPI_CATEGORY_IMPORT_ENTITY_TYPE,
} from 'constants';

import PropTypes from 'prop-types';

export const KPITemplatePropTypes = PropTypes.shape({
  name: PropTypes.string,
  description: PropTypes.string,
  moduleAndTargetName: PropTypes.string,
  packageName: PropTypes.string,
  distributionName: PropTypes.string,
  url: PropTypes.string,
  category: PropTypes.oneOf([
    KPI_CATEGORY_TRANSFORMER,
    KPI_CATEGORY_AGGREGATOR,
    KPI_CATEGORY_ALERT,
    KPI_CATEGORY_IMPORT_ENTITY_TYPE,
  ]),
  output: PropTypes.arrayOf(PropTypes.object),
  input: PropTypes.arrayOf(PropTypes.object),
});

export const KPIPipelineLogPropTypes = PropTypes.shape({
  entityTypeId: PropTypes.number,
  operation: PropTypes.string,
  status: PropTypes.string,
  currentStageName: PropTypes.string,
  currentStage: PropTypes.number,
  totalStages: PropTypes.number,
  message: PropTypes.string,
  errorType: PropTypes.string,
  errorMessage: PropTypes.string,
  errorStacktrace: PropTypes.string,
  logFileContent: PropTypes.string,
  logfilePath: PropTypes.string,
  createdTimestamp: PropTypes.string,
  updatedTimestamp: PropTypes.string,
});

/** Array of logs */
export const KPIPipelineLogsPropTypes = PropTypes.arrayOf(KPIPipelineLogPropTypes);

export const KPIPipelineErrorPropTypes = PropTypes.shape({
  lastSuccessfulRun: KPIPipelineLogPropTypes,
  lastFailedRun: KPIPipelineLogPropTypes,
  onShowLog: PropTypes.func,
});

export const KPITemplatesPropTypes = PropTypes.arrayOf(KPITemplatePropTypes);

export const KPIPropTypes = PropTypes.shape({
  name: PropTypes.string,
  description: PropTypes.string,
  moduleAndTargetName: PropTypes.string,
  packageName: PropTypes.string,
  distributionName: PropTypes.string,
  url: PropTypes.string,
  category: PropTypes.oneOf([
    KPI_CATEGORY_TRANSFORMER,
    KPI_CATEGORY_AGGREGATOR,
    KPI_CATEGORY_ALERT,
    KPI_CATEGORY_IMPORT_ENTITY_TYPE,
  ]),
  output: PropTypes.oneOfType([PropTypes.array, PropTypes.objectOf(PropTypes.any)]),
  input: PropTypes.oneOfType([PropTypes.array, PropTypes.objectOf(PropTypes.any)]),
});

export const KPIsPropTypes = PropTypes.arrayOf(KPIPropTypes);

export const KPIInputPropTypes = PropTypes.shape({
  name: PropTypes.string,
  description: PropTypes.string,
  type: PropTypes.oneOf([INPUT_TYPE_DATA_ITEM, INPUT_TYPE_CONSTANT, INPUT_TYPE_DOMAIN]),
  dataType: PropTypes.oneOf([
    COLUMNTYPE_NUMBER,
    COLUMNTYPE_TIMESTAMP,
    COLUMNTYPE_BOOLEAN,
    COLUMNTYPE_LITERAL,
    COLUMNTYPE_JSON,
    COLUMNTYPE_ARRAY,
  ]),
  values: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  required: PropTypes.bool,
  /* optional schema contains cardinality */
  jsonSchema: PropTypes.object, // eslint-disable-line react/forbid-prop-types
});

export const ConstantPropTypes = PropTypes.shape({
  name: PropTypes.string,
  enabled: PropTypes.bool,
  entityType: PropTypes.string,
  metadata: PropTypes.shape({
    dataType: PropTypes.oneOf([
      COLUMNTYPE_NUMBER,
      COLUMNTYPE_TIMESTAMP,
      COLUMNTYPE_BOOLEAN,
      COLUMNTYPE_LITERAL,
      COLUMNTYPE_JSON,
      COLUMNTYPE_ARRAY,
    ]),
  }),
  value: PropTypes.any, // eslint-disable-line react/forbid-prop-types
});

/** set of constants that can be configured at an entity type level */
export const ConstantsPropTypes = PropTypes.arrayOf(ConstantPropTypes);

/**
 * Domain prop types
 */
export const DomainPropTypes = PropTypes.shape({
  name: PropTypes.string,
  description: PropTypes.string,
  type: PropTypes.oneOf(['LITERAL']),
  value: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  /* carbon icon name */
  iconName: PropTypes.string,
  /* hex color to show for this icon */
  iconColor: PropTypes.string,
});

/** multiple domains */
export const DomainsPropTypes = PropTypes.arrayOf(DomainPropTypes);

export const GranularityPropTypes = PropTypes.shape({
  name: PropTypes.string,
  frequency: PropTypes.oneOf(['Weekly', 'Daily', 'Hourly', 'Monthly', 'Yearly', 'Minute']),
  default: PropTypes.bool,
  tenant: PropTypes.string,
  dimensions: PropTypes.arrayOf(PropTypes.string),
});

export const GranularitiesPropTypes = PropTypes.arrayOf(GranularityPropTypes);

export const AggregatePropTypes = PropTypes.shape({ name: PropTypes.string });

export const AggregatesPropTypes = PropTypes.arrayOf(AggregatePropTypes);
